import React, { useMemo, useState } from 'react';
import { mockExperiences } from '../data/mockExperiences';
import { ExperienceRecord, IndividualExperience, VerificationStatus, ExitType } from '../types/experience';
import { Condition, ConditionType } from '../types/condition';
import { RecordDrawer } from './RecordDrawer';
import { Badge, StatusBadge } from './ui/Badge';
import { GuarantorReview } from './GuarantorReview';
import { CreateConditionModal } from './CreateConditionModal';
import { ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, LayoutGridIcon, TableIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, XIcon, SkipForwardIcon, SaveIcon, AlertCircleIcon } from 'lucide-react';
type SortKey = keyof IndividualExperience | 'Guarantor__c' | 'Net_Profit__c';
type SortDirection = 'asc' | 'desc';
interface FlattenedRecord {
  experience: IndividualExperience;
  guarantor: ExperienceRecord;
}
// Exit type badge — compact labels for table display
function ExitTypeBadge({
  exitType
}: {
  exitType: ExitType;
}) {
  if (!exitType) {
    return <span className="text-gray-300 text-xs" data-id="element-203">—</span>;
  }
  switch (exitType) {
    case 'Sale':
      return <Badge variant="success" className="whitespace-nowrap" data-id="element-204">
          Sale
        </Badge>;
    case 'Rental Hold - Refinance':
      return <Badge variant="default" className="whitespace-nowrap" data-id="element-205">
          RH Refi
        </Badge>;
    case 'Rental Hold - No Refinance':
      return <Badge variant="warning" className="whitespace-nowrap" data-id="element-206">
          RH No Refi
        </Badge>;
    case 'No Exit':
      return <Badge variant="secondary" className="whitespace-nowrap" data-id="element-207">
          No Exit
        </Badge>;
    default:
      return <span className="text-gray-300 text-xs" data-id="element-208">—</span>;
  }
}
export function TriageTable() {
  const [data, setData] = useState<ExperienceRecord[]>(mockExperiences);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'review'>('table');
  const [pickedGuarantorId, setPickedGuarantorId] = useState<string | null>(null);
  const [guarantorSaved, setGuarantorSaved] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Conditions State
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [conditionTargetIds, setConditionTargetIds] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  }>({
    key: 'Guarantor__c',
    direction: 'asc'
  });
  const [selectedGuarantor, setSelectedGuarantor] = useState<string>('all');
  const [purchaseSortField, setPurchaseSortField] = useState<'Purchase_Price__c' | 'Purchase_Date__c'>('Purchase_Price__c');
  const [saleSortField, setSaleSortField] = useState<'Sale_Price__c' | 'Sale_Date__c'>('Sale_Price__c');
  // Flatten nested data structure for table view
  const flattenedData = useMemo(() => {
    const flat: FlattenedRecord[] = [];
    data.forEach(guarantorRecord => {
      if (guarantorRecord.individualExperiences) {
        guarantorRecord.individualExperiences.forEach(exp => {
          flat.push({
            experience: exp,
            guarantor: guarantorRecord
          });
        });
      }
    });
    return flat;
  }, [data]);
  // Derive unique guarantor names from data
  const guarantors = useMemo(() => {
    const unique = Array.from(new Set(data.map(d => d.Guarantor__c).filter(Boolean)));
    return unique.sort() as string[];
  }, [data]);
  // Filter and Sort
  const processedData = useMemo(() => {
    let result = [...flattenedData];
    if (selectedGuarantor !== 'all') {
      result = result.filter(item => item.guarantor.Guarantor__c === selectedGuarantor);
    }
    result.sort((a, b) => {
      let aValue: any;
      let bValue: any;
      if (sortConfig.key === 'Guarantor__c') {
        aValue = a.guarantor.Guarantor__c;
        bValue = b.guarantor.Guarantor__c;
      } else {
        aValue = a.experience[sortConfig.key as keyof IndividualExperience];
        bValue = b.experience[sortConfig.key as keyof IndividualExperience];
      }
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [flattenedData, sortConfig, selectedGuarantor]);
  // Recompute aggregates from only verified individual experiences
  const reviewData = useMemo(() => {
    let filtered = data;
    if (selectedGuarantor !== 'all') {
      filtered = filtered.filter(g => g.Guarantor__c === selectedGuarantor);
    }
    return filtered.map(guarantor => {
      const verified = (guarantor.individualExperiences || []).filter(exp => exp.triageStatus === 'verified');
      const flips = verified.filter(e => e.Experience_Type__c === 'Fix & Flip');
      const newBuilds = verified.filter(e => e.Experience_Type__c === 'New Construction');
      const gcProjects = verified.filter(e => e.Experience_Type__c === 'No Rehab');
      const calcDurationMonths = (exp: IndividualExperience): number | null => {
        if (!exp.Purchase_Date__c || !exp.Sale_Date__c) return null;
        const start = new Date(exp.Purchase_Date__c);
        const end = new Date(exp.Sale_Date__c);
        return (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
      };
      const avg = (vals: (number | null)[]): number | null => {
        const valid = vals.filter((v): v is number => v !== null && v !== undefined);
        return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
      };
      const sum = (vals: (number | null)[]): number | null => {
        const valid = vals.filter((v): v is number => v !== null && v !== undefined);
        return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) : null;
      };
      const max = (vals: (number | null)[]): number | null => {
        const valid = vals.filter((v): v is number => v !== null && v !== undefined);
        return valid.length > 0 ? Math.max(...valid) : null;
      };
      const rehabExps = verified.filter(e => e.Rehab_Flag__c);
      const constructionExps = verified.filter(e => e.Construction_Flag__c);
      const allDurations = verified.map(calcDurationMonths);
      const flipDurations = flips.map(calcDurationMonths);
      const newBuildDurations = newBuilds.map(calcDurationMonths);
      return {
        ...guarantor,
        Total_Experience__c: verified.length,
        Transactions_Cumulative_Sale_Price__c: sum(verified.map(e => e.Sale_Price__c)),
        Transactions_Average_Sales_Price__c: avg(verified.map(e => e.Sale_Price__c)),
        Transactions_Average_Project_Duration__c: avg(allDurations),
        Number_of_Flips__c: flips.length || null,
        Fix_Flip_Cumulative_Sale_Price__c: sum(flips.map(e => e.Sale_Price__c)),
        Fix_Flip_Average_Sale_Price__c: avg(flips.map(e => e.Sale_Price__c)),
        Fix_Flip_Average_Project_Duration__c: avg(flipDurations),
        Number_of_New_Builds__c: newBuilds.length || null,
        New_Construction_Cumulative_Sale_Price__c: sum(newBuilds.map(e => e.Sale_Price__c)),
        New_Construction_Average_Sale_Price__c: avg(newBuilds.map(e => e.Sale_Price__c)),
        New_Construction_Avg_Project_Duration__c: avg(newBuildDurations),
        Number_of_GC_Projects__c: gcProjects.length || null,
        Average_Rehab_Budget__c: avg(rehabExps.map(e => e.Rehab_Amount__c)),
        Max_Rehab_Budget__c: max(rehabExps.map(e => e.Rehab_Amount__c)),
        Average_GC_Budget__c: avg(gcProjects.map(e => e.Rehab_Amount__c)),
        Max_GC_Budget__c: max(gcProjects.map(e => e.Rehab_Amount__c)),
        Average_Construction_Budget__c: avg(constructionExps.map(e => e.Rehab_Amount__c)),
        Max_Construction_Budget__c: max(constructionExps.map(e => e.Rehab_Amount__c))
      } as ExperienceRecord;
    });
  }, [data, selectedGuarantor]);
  const pickedGuarantor = useMemo(() => {
    if (!pickedGuarantorId) return null;
    return reviewData.find(g => g.Id === pickedGuarantorId) || null;
  }, [pickedGuarantorId, reviewData]);
  const handlePickGuarantor = (id: string | null) => {
    setPickedGuarantorId(id);
    setGuarantorSaved(false);
  };
  const handleSaveGuarantor = () => {
    setGuarantorSaved(true);
    setTimeout(() => setGuarantorSaved(false), 2500);
  };
  const handleSort = (key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  const handlePurchaseSort = () => {
    if (sortConfig.key === purchaseSortField) {
      if (sortConfig.direction === 'desc') {
        const nextField = purchaseSortField === 'Purchase_Price__c' ? 'Purchase_Date__c' : 'Purchase_Price__c';
        setPurchaseSortField(nextField);
        setSortConfig({
          key: nextField as SortKey,
          direction: 'asc'
        });
      } else {
        setSortConfig({
          key: purchaseSortField as SortKey,
          direction: 'desc'
        });
      }
    } else {
      setSortConfig({
        key: purchaseSortField as SortKey,
        direction: 'asc'
      });
    }
  };
  const handleSaleSort = () => {
    if (sortConfig.key === saleSortField) {
      if (sortConfig.direction === 'desc') {
        const nextField = saleSortField === 'Sale_Price__c' ? 'Sale_Date__c' : 'Sale_Price__c';
        setSaleSortField(nextField);
        setSortConfig({
          key: nextField as SortKey,
          direction: 'asc'
        });
      } else {
        setSortConfig({
          key: saleSortField as SortKey,
          direction: 'desc'
        });
      }
    } else {
      setSortConfig({
        key: saleSortField as SortKey,
        direction: 'asc'
      });
    }
  };
  const handleUpdateStatus = (id: string, status: VerificationStatus) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => exp.Id === id ? {
        ...exp,
        triageStatus: status
      } : exp) || []
    })));
    setSelectedRecordId(null);
  };
  const handleUpdateNotes = (id: string, notes: string) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => exp.Id === id ? {
        ...exp,
        triageNotes: notes
      } : exp) || []
    })));
  };
  const handleUpdateRecord = (id: string, updates: Partial<IndividualExperience>) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => {
        if (exp.Id === id) {
          const updatedExp = {
            ...exp,
            ...updates
          };
          if ('Sale_Price__c' in updates || 'Purchase_Price__c' in updates || 'Rehab_Amount__c' in updates) {
            const sale = updatedExp.Sale_Price__c ?? 0;
            const purchase = updatedExp.Purchase_Price__c ?? 0;
            const rehab = updatedExp.Rehab_Amount__c ?? 0;
            updatedExp.Net_Profit__c = sale - (purchase + rehab);
          }
          return updatedExp;
        }
        return exp;
      }) || []
    })));
  };
  const handleBulkUpdateStatus = (status: VerificationStatus) => {
    setData(prevData => prevData.map(guarantorRecord => ({
      ...guarantorRecord,
      individualExperiences: guarantorRecord.individualExperiences?.map(exp => selectedIds.has(exp.Id) ? {
        ...exp,
        triageStatus: status
      } : exp) || []
    })));
    setSelectedIds(new Set());
  };
  const handleOpenBulkConditionModal = () => {
    setConditionTargetIds(Array.from(selectedIds));
    setConditionModalOpen(true);
  };
  const handleOpenSingleConditionModal = (id: string) => {
    setConditionTargetIds([id]);
    setConditionModalOpen(true);
  };
  const handleCreateCondition = (type: ConditionType, notes: string) => {
    const newCondition: Condition = {
      id: crypto.randomUUID(),
      type,
      notes,
      experienceIds: conditionTargetIds,
      createdAt: new Date().toISOString()
    };
    setConditions(prev => [...prev, newCondition]);
    setConditionModalOpen(false);
    if (selectedIds.size > 0 && conditionTargetIds.length > 1) {
      setSelectedIds(new Set());
    }
  };
  const getConditionsForRecord = (id: string) => {
    return conditions.filter(c => c.experienceIds.includes(id));
  };
  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleToggleSelectAll = () => {
    const visibleIds = processedData.map(item => item.experience.Id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    if (allSelected) setSelectedIds(new Set());else setSelectedIds(new Set(visibleIds));
  };
  const selectedItem = flattenedData.find(item => item.experience.Id === selectedRecordId);
  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const formatShortDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };
  const getRowClass = (status?: VerificationStatus, isSelected?: boolean) => {
    if (isSelected) {
      switch (status) {
        case 'verified':
          return 'bg-green-100/70 hover:bg-green-100';
        case 'flagged':
          return 'bg-red-100/70 hover:bg-red-100';
        default:
          return 'bg-blue-50 hover:bg-blue-100/70';
      }
    }
    switch (status) {
      case 'verified':
        return 'bg-green-50/50 hover:bg-green-100/50';
      case 'flagged':
        return 'bg-red-50/50 hover:bg-red-100/50';
      case 'skipped':
        return 'bg-gray-50/50 hover:bg-gray-100/50 opacity-60';
      default:
        return 'hover:bg-blue-50/50';
    }
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: SortKey;
  }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDownIcon className="w-3 h-3 ml-1 text-gray-300" data-id="element-209" />;
    return sortConfig.direction === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1 text-blue-600" data-id="element-210" /> : <ArrowDownIcon className="w-3 h-3 ml-1 text-blue-600" data-id="element-211" />;
  };
  return <div className="flex flex-col h-screen bg-gray-50 overflow-hidden font-sans" data-id="element-212">
      {/* Top Bar */}
      <div className="h-14 bg-gray-900 flex items-center px-5 flex-shrink-0 z-30 justify-between" data-id="element-213">
        <div className="flex items-center space-x-4" data-id="element-214">
          <div className="h-5 w-px bg-gray-700 mx-1" data-id="element-215"></div>
          <button onClick={() => setViewMode('table')} className={`flex items-center space-x-2 px-4 py-2 rounded transition-all text-sm font-semibold ${viewMode === 'table' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`} data-id="element-216">
            <TableIcon className="w-4 h-4" data-id="element-217" />
            <span data-id="element-218">Triage</span>
          </button>
          <button onClick={() => setViewMode('review')} className={`flex items-center space-x-2 px-4 py-2 rounded transition-all text-sm font-semibold ${viewMode === 'review' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`} data-id="element-219">
            <LayoutGridIcon className="w-4 h-4" data-id="element-220" />
            <span data-id="element-221">Review</span>
          </button>
        </div>

        {/* Right side: contextual actions */}
        <div className="flex items-center space-x-2" data-id="element-222">
          {selectedIds.size > 0 && viewMode === 'table' && <>
              <span className="flex items-center space-x-2 text-sm text-gray-300 mr-1" data-id="element-223">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold" data-id="element-224">
                  {selectedIds.size}
                </span>
                <span className="hidden sm:inline" data-id="element-225">selected</span>
              </span>
              <button onClick={() => handleBulkUpdateStatus('verified')} className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold transition-colors" data-id="element-226">
                <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-227" />
                <span data-id="element-228">Verify</span>
              </button>
              <button onClick={() => handleBulkUpdateStatus('flagged')} className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold transition-colors" data-id="element-229">
                <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-230" />
                <span data-id="element-231">Flag</span>
              </button>
              <button onClick={() => handleBulkUpdateStatus('skipped')} className="flex items-center space-x-1.5 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs font-bold transition-colors" data-id="element-232">
                <SkipForwardIcon className="w-3.5 h-3.5" data-id="element-233" />
                <span data-id="element-234">Skip</span>
              </button>
              <button onClick={handleOpenBulkConditionModal} className="flex items-center space-x-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-bold transition-colors" data-id="element-235">
                <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-236" />
                <span data-id="element-237">Condition</span>
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="flex items-center space-x-1 px-2 py-1.5 text-gray-400 hover:text-white text-xs transition-colors" data-id="element-238">
                <XIcon className="w-3.5 h-3.5" data-id="element-239" />
              </button>
            </>}

          {viewMode === 'review' && pickedGuarantor && <>
              <div className="flex items-center space-x-2 text-sm text-gray-300" data-id="element-240">
                <CheckCircleIcon className="w-4 h-4 text-blue-400" data-id="element-241" />
                <span className="font-semibold text-white" data-id="element-242">
                  {pickedGuarantor.Guarantor__c}
                </span>
              </div>
              <button onClick={handleSaveGuarantor} disabled={guarantorSaved} className={`flex items-center space-x-1.5 px-3 py-1.5 rounded text-xs font-bold transition-colors ${guarantorSaved ? 'bg-green-600 text-green-100 cursor-default' : 'bg-white text-gray-900 hover:bg-gray-100'}`} data-id="element-243">
                {guarantorSaved ? <>
                    <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-244" />
                    <span data-id="element-245">Saved ✓</span>
                  </> : <>
                    <SaveIcon className="w-3.5 h-3.5" data-id="element-246" />
                    <span data-id="element-247">Save Selection</span>
                  </>}
              </button>
              <button onClick={() => handlePickGuarantor(null)} className="flex items-center space-x-1 px-2 py-1.5 text-gray-400 hover:text-white text-xs transition-colors" data-id="element-248">
                <XIcon className="w-3.5 h-3.5" data-id="element-249" />
              </button>
            </>}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden" data-id="element-250">
        {/* Guarantor Filter Bar */}
        <div className="h-14 bg-white border-b border-gray-200 px-5 flex items-center justify-between flex-shrink-0 z-20" data-id="element-251">
          <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar" data-id="element-252">
            <button onClick={() => setSelectedGuarantor('all')} className={`px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${selectedGuarantor === 'all' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-253">
              All
            </button>
            {guarantors.map(g => <button key={g} onClick={() => setSelectedGuarantor(g)} className={`px-3.5 py-1.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${selectedGuarantor === g ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-254">
                {g}
              </button>)}
          </div>
          <div className="flex items-center text-sm text-gray-500 ml-4 flex-shrink-0" data-id="element-255">
            <span className="font-bold text-gray-900 mr-1" data-id="element-256">
              {viewMode === 'table' ? processedData.length : reviewData.length}
            </span>{' '}
            {viewMode === 'table' ? 'records' : 'guarantors'}
          </div>
        </div>

        {/* Content Area */}
        {viewMode === 'review' ? <GuarantorReview data={reviewData} pickedGuarantorId={pickedGuarantorId} onPickGuarantor={handlePickGuarantor} data-id="element-257" /> : <div className="flex-1 overflow-auto bg-white" data-id="element-258">
            <table className="min-w-full divide-y divide-gray-200 border-collapse" data-id="element-259">
              <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm" data-id="element-260">
                <tr data-id="element-261">
                  {/* Checkbox */}
                  <th scope="col" className="w-10 px-3 py-3.5 border-b border-gray-200" data-id="element-262">
                    <input type="checkbox" checked={processedData.length > 0 && processedData.every(item => selectedIds.has(item.experience.Id))} onChange={handleToggleSelectAll} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-263" />
                  </th>
                  {/* Type */}
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Experience_Type__c')} data-id="element-264">
                    <div className="flex items-center" data-id="element-265">
                      Type <SortIcon columnKey="Experience_Type__c" data-id="element-266" />
                    </div>
                  </th>
                  {/* Exit — NEW column */}
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-267">
                    Exit
                  </th>
                  {/* Property Address */}
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-268">
                    Property Address
                  </th>
                  {/* Guarantor */}
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Guarantor__c')} data-id="element-269">
                    <div className="flex items-center" data-id="element-270">
                      Guarantor <SortIcon columnKey="Guarantor__c" data-id="element-271" />
                    </div>
                  </th>
                  {/* Entity Name */}
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-272">
                    Entity Name
                  </th>
                  {/* Purchase */}
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={handlePurchaseSort} data-id="element-273">
                    <div className="flex items-center justify-end gap-1.5" data-id="element-274">
                      <span className="flex items-center gap-1" data-id="element-275">
                        Purchase
                        {sortConfig.key === 'Purchase_Price__c' || sortConfig.key === 'Purchase_Date__c' ? purchaseSortField === 'Purchase_Price__c' ? <DollarSignIcon className="w-3 h-3 text-blue-500" data-id="element-276" /> : <CalendarIcon className="w-3 h-3 text-blue-500" data-id="element-277" /> : <DollarSignIcon className="w-3 h-3 text-gray-300" data-id="element-278" />}
                      </span>
                      <SortIcon columnKey={purchaseSortField as SortKey} data-id="element-279" />
                    </div>
                  </th>
                  {/* Sale */}
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={handleSaleSort} data-id="element-280">
                    <div className="flex items-center justify-end gap-1.5" data-id="element-281">
                      <span className="flex items-center gap-1" data-id="element-282">
                        Sale
                        {sortConfig.key === 'Sale_Price__c' || sortConfig.key === 'Sale_Date__c' ? saleSortField === 'Sale_Price__c' ? <DollarSignIcon className="w-3 h-3 text-blue-500" data-id="element-283" /> : <CalendarIcon className="w-3 h-3 text-blue-500" data-id="element-284" /> : <DollarSignIcon className="w-3 h-3 text-gray-300" data-id="element-285" />}
                      </span>
                      <SortIcon columnKey={saleSortField as SortKey} data-id="element-286" />
                    </div>
                  </th>
                  {/* Budget (was Rehab) */}
                  <th scope="col" className="px-5 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-b border-gray-200" onClick={() => handleSort('Rehab_Amount__c')} data-id="element-287">
                    <div className="flex items-center justify-end" data-id="element-288">
                      Budget <SortIcon columnKey="Rehab_Amount__c" data-id="element-289" />
                    </div>
                  </th>
                  {/* Status */}
                  <th scope="col" className="px-5 py-3.5 text-center text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-290">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100" data-id="element-291">
                {processedData.map(({
              experience,
              guarantor
            }) => {
              const recordConditions = getConditionsForRecord(experience.Id);
              const hasConditions = recordConditions.length > 0;
              return <tr key={experience.Id} onClick={() => setSelectedRecordId(experience.Id)} className={`cursor-pointer transition-colors ${getRowClass(experience.triageStatus, selectedIds.has(experience.Id))}`} data-id="element-292">
                      {/* Checkbox */}
                      <td className="w-10 px-3 py-3.5" onClick={e => e.stopPropagation()} data-id="element-293">
                        <input type="checkbox" checked={selectedIds.has(experience.Id)} onChange={() => handleToggleSelect(experience.Id)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-294" />
                      </td>

                      {/* Type + flags */}
                      <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-295">
                        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-296">
                          <Badge variant="outline" data-id="element-297">
                            {experience.Experience_Type__c}
                          </Badge>
                          {experience.ABL_RT_Flag__c && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200 uppercase tracking-wider" data-id="element-298">
                              ABL RT
                            </span>}
                          {experience.GC_Flag__c && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-100 text-cyan-700 border border-cyan-200 uppercase tracking-wider" data-id="element-299">
                              GC
                            </span>}
                          {experience.Sponsor_Flag__c && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200 uppercase tracking-wider" data-id="element-300">
                              Sponsor
                            </span>}
                          {hasConditions && <div className="relative group" data-id="element-301">
                              <AlertCircleIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-302" />
                              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 bg-gray-900 text-white text-xs rounded p-2 z-50 shadow-lg" data-id="element-303">
                                {recordConditions.length} condition
                                {recordConditions.length > 1 ? 's' : ''} active
                              </div>
                            </div>}
                        </div>
                      </td>

                      {/* Exit Type */}
                      <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-304">
                        <ExitTypeBadge exitType={experience.Exit_Type__c} data-id="element-305" />
                      </td>

                      {/* Property Address */}
                      <td className="px-5 py-3.5" data-id="element-306">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-307">
                          {experience.Property_Address__c.street}
                        </div>
                        <div className="text-xs text-gray-500 truncate mt-0.5" data-id="element-308">
                          {experience.Property_Address__c.city},{' '}
                          {experience.Property_Address__c.state}
                        </div>
                      </td>

                      {/* Guarantor */}
                      <td className="px-5 py-3.5 whitespace-nowrap" data-id="element-309">
                        <div className="text-sm font-medium text-gray-900" data-id="element-310">
                          {guarantor.Guarantor__c}
                        </div>
                      </td>

                      {/* Entity Name */}
                      <td className="px-5 py-3.5" data-id="element-311">
                        <div className="text-sm text-gray-900 truncate max-w-[160px]" data-id="element-312">
                          {experience.Entity_Name__c || <span className="text-gray-400" data-id="element-313">—</span>}
                        </div>
                      </td>

                      {/* Purchase */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-right" data-id="element-314">
                        <div className="text-sm tabular-nums text-gray-600" data-id="element-315">
                          {formatCurrency(experience.Purchase_Price__c)}
                        </div>
                        {formatShortDate(experience.Purchase_Date__c) && <div className="text-[10px] text-gray-400 mt-0.5" data-id="element-316">
                            {formatShortDate(experience.Purchase_Date__c)}
                          </div>}
                      </td>

                      {/* Sale */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-right" data-id="element-317">
                        <div className="text-sm tabular-nums text-gray-600" data-id="element-318">
                          {formatCurrency(experience.Sale_Price__c)}
                        </div>
                        {formatShortDate(experience.Sale_Date__c) && <div className="text-[10px] text-gray-400 mt-0.5" data-id="element-319">
                            {formatShortDate(experience.Sale_Date__c)}
                          </div>}
                      </td>

                      {/* Budget (was Rehab) */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-right text-sm tabular-nums text-gray-600" data-id="element-320">
                        {formatCurrency(experience.Rehab_Amount__c)}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-3.5 whitespace-nowrap text-center" data-id="element-321">
                        <StatusBadge status={experience.triageStatus || 'pending'} data-id="element-322" />
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>

            {processedData.length === 0 && <div className="p-16 text-center text-gray-400 text-sm" data-id="element-323">
                No records found for this guarantor.
              </div>}
          </div>}

        {/* Detail Drawer */}
        <RecordDrawer isOpen={!!selectedRecordId} onClose={() => setSelectedRecordId(null)} record={selectedItem?.experience || null} guarantor={selectedItem?.guarantor || null} onVerify={id => handleUpdateStatus(id, 'verified')} onFlag={id => handleUpdateStatus(id, 'flagged')} onSkip={id => handleUpdateStatus(id, 'skipped')} onUpdateNotes={handleUpdateNotes} onUpdateRecord={handleUpdateRecord} conditions={selectedRecordId ? getConditionsForRecord(selectedRecordId) : []} onCreateCondition={() => selectedRecordId && handleOpenSingleConditionModal(selectedRecordId)} data-id="element-324" />

        {/* Create Condition Modal */}
        <CreateConditionModal isOpen={conditionModalOpen} onClose={() => setConditionModalOpen(false)} onSubmit={handleCreateCondition} targetCount={conditionTargetIds.length} targetLabel={conditionTargetIds.length === 1 ? flattenedData.find(i => i.experience.Id === conditionTargetIds[0])?.experience.Name : undefined} data-id="element-325" />
      </div>
    </div>;
}